import { Component, OnInit, OnDestroy, Input, inject, PLATFORM_ID, NgZone } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';

@Component({
    selector: 'app-easter-flip-clock',
    imports: [CommonModule],
    template: `
    @if (displayStyle === 'pill') {
      <div
        class="tw-rounded-2xl md:tw-rounded-full tw-px-4 md:tw-px-6 tw-py-3 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-x-2 tw-gap-y-1"
        style="background-color: #ff8c00"
        aria-live="polite"
        [attr.aria-label]="ariaLabel"
        >
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl tw-uppercase tw-tracking-wide tw-whitespace-nowrap">{{ days }} DAYS</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl">:</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl tw-uppercase tw-tracking-wide tw-whitespace-nowrap">{{ hours | number:'2.0-0' }} HOURS</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl">:</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl tw-uppercase tw-tracking-wide tw-whitespace-nowrap">{{ minutes | number:'2.0-0' }} MINUTES</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl">:</span>
        <span class="tw-font-poppins tw-font-bold tw-text-black tw-text-xs sm:tw-text-sm md:tw-text-xl tw-uppercase tw-tracking-wide tw-whitespace-nowrap">{{ seconds | number:'2.0-0' }} SECONDS</span>
      </div>
    } @else {
      <div aria-live="polite" [attr.aria-label]="ariaLabel">
        <!-- Row 1: tiles + colons, all vertically centered -->
        <div class="tw-flex tw-justify-center tw-items-center tw-gap-2 md:tw-gap-3">
          <!-- Days tiles -->
          <div class="tw-flex tw-gap-1 md:tw-gap-2">
            @for (digit of dayDigits; track $index; let i = $index) {
              <div
                class="sf-tile tw-w-6 md:tw-w-12"
                [class.flipping]="flipStates.days[i]"
                >
                <div class="sf-upper"><span class="tw-font-poppins tw-font-bold">{{ nextDayDigits[i] || '0' }}</span></div>
                <div class="sf-lower"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-lower-next"><span class="tw-font-poppins tw-font-bold">{{ nextDayDigits[i] || '0' }}</span></div>
                <div class="sf-flap"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-divider"></div>
              </div>
            }
          </div>
          <span class="tw-font-poppins tw-font-bold tw-text-white tw-text-lg md:tw-text-3xl tw-leading-none">:</span>
          <!-- Hours tiles -->
          <div class="tw-flex tw-gap-1 md:tw-gap-2">
            @for (digit of hourDigits; track $index; let i = $index) {
              <div
                class="sf-tile tw-w-6 md:tw-w-12"
                [class.flipping]="flipStates.hours[i]"
                >
                <div class="sf-upper"><span class="tw-font-poppins tw-font-bold">{{ nextHourDigits[i] || '0' }}</span></div>
                <div class="sf-lower"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-lower-next"><span class="tw-font-poppins tw-font-bold">{{ nextHourDigits[i] || '0' }}</span></div>
                <div class="sf-flap"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-divider"></div>
              </div>
            }
          </div>
          <span class="tw-font-poppins tw-font-bold tw-text-white tw-text-lg md:tw-text-3xl tw-leading-none">:</span>
          <!-- Minutes tiles -->
          <div class="tw-flex tw-gap-1 md:tw-gap-2">
            @for (digit of minuteDigits; track $index; let i = $index) {
              <div
                class="sf-tile tw-w-6 md:tw-w-12"
                [class.flipping]="flipStates.minutes[i]"
                >
                <div class="sf-upper"><span class="tw-font-poppins tw-font-bold">{{ nextMinuteDigits[i] || '0' }}</span></div>
                <div class="sf-lower"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-lower-next"><span class="tw-font-poppins tw-font-bold">{{ nextMinuteDigits[i] || '0' }}</span></div>
                <div class="sf-flap"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-divider"></div>
              </div>
            }
          </div>
          <span class="tw-font-poppins tw-font-bold tw-text-white tw-text-lg md:tw-text-3xl tw-leading-none">:</span>
          <!-- Seconds tiles -->
          <div class="tw-flex tw-gap-1 md:tw-gap-2">
            @for (digit of secondDigits; track $index; let i = $index) {
              <div
                class="sf-tile tw-w-6 md:tw-w-12"
                [class.flipping]="flipStates.seconds[i]"
                >
                <div class="sf-upper"><span class="tw-font-poppins tw-font-bold">{{ nextSecondDigits[i] || '0' }}</span></div>
                <div class="sf-lower"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-lower-next"><span class="tw-font-poppins tw-font-bold">{{ nextSecondDigits[i] || '0' }}</span></div>
                <div class="sf-flap"><span class="tw-font-poppins tw-font-bold">{{ digit }}</span></div>
                <div class="sf-divider"></div>
              </div>
            }
          </div>
        </div>
        <!-- Row 2: labels aligned under each tile group -->
        <div class="tw-flex tw-justify-center tw-items-center tw-gap-2 md:tw-gap-3 tw-mt-2">
          <span class="tw-text-xs md:tw-text-sm tw-text-v4-yellow-1 tw-font-poppins tw-font-semibold tw-tracking-widest tw-uppercase tw-w-[52px] md:tw-w-[104px] tw-text-center">Days</span>
          <span class="tw-text-lg md:tw-text-3xl tw-invisible">:</span>
          <span class="tw-text-xs md:tw-text-sm tw-text-v4-yellow-1 tw-font-poppins tw-font-semibold tw-tracking-widest tw-uppercase tw-w-[52px] md:tw-w-[104px] tw-text-center">Hours</span>
          <span class="tw-text-lg md:tw-text-3xl tw-invisible">:</span>
          <span class="tw-text-xs md:tw-text-sm tw-text-v4-yellow-1 tw-font-poppins tw-font-semibold tw-tracking-widest tw-uppercase tw-w-[52px] md:tw-w-[104px] tw-text-center">Minutes</span>
          <span class="tw-text-lg md:tw-text-3xl tw-invisible">:</span>
          <span class="tw-text-xs md:tw-text-sm tw-text-v4-yellow-1 tw-font-poppins tw-font-semibold tw-tracking-widest tw-uppercase tw-w-[52px] md:tw-w-[104px] tw-text-center">Seconds</span>
        </div>
      </div>
    }
    
    `,
    styleUrl: './flip-clock.component.scss'
})
export class EasterFlipClockComponent implements OnInit, OnDestroy {
  private zone = inject(NgZone);
  @Input() targetDate: string = '2026-04-19T00:00:00';
  @Input() displayStyle: 'flip' | 'pill' = 'flip';
  days: number = 0;
  hours: number = 0;
  minutes: number = 0;
  seconds: number = 0;
  dayDigits: string[] = [];
  hourDigits: string[] = [];
  minuteDigits: string[] = [];
  secondDigits: string[] = [];
  nextDayDigits: string[] = [];
  nextHourDigits: string[] = [];
  nextMinuteDigits: string[] = [];
  nextSecondDigits: string[] = [];
  ariaLabel: string = '';
  flipStates: {
    days: boolean[];
    hours: boolean[];
    minutes: boolean[];
    seconds: boolean[];
  } = {
    days: [],
    hours: [],
    minutes: [],
    seconds: [],
  };
  private intervalId: any;
  private platformId = inject(PLATFORM_ID);

  ngOnInit() {
    if(isPlatformBrowser(this.platformId)) {
      this.updateCountdown();
      // Outside Angular so the app can become stable (hydration, NG0506);
      // each tick re-enters it to update the view.
      this.zone.runOutsideAngular(() => {
        this.intervalId = setInterval(() => this.zone.run(() => this.updateCountdown()), 1000);
      });
    }
  }

  ngOnDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  private updateCountdown() {
    const now = new Date();
    const target = new Date(this.targetDate);
    const diff = target.getTime() - now.getTime();

    if (diff <= 0) {
      this.ariaLabel = 'Event has started!';
      clearInterval(this.intervalId);
      return;
    }

    const newDays = Math.floor(diff / (1000 * 60 * 60 * 24));
    const newHours = Math.floor(
      (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const newMinutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const newSeconds = Math.floor((diff % (1000 * 60)) / 1000);

    const newDayDigits = String(newDays).padStart(2, '0').split('');
    const newHourDigits = String(newHours).padStart(2, '0').split('');
    const newMinuteDigits = String(newMinutes).padStart(2, '0').split('');
    const newSecondDigits = String(newSeconds).padStart(2, '0').split('');

    if (!this.flipStates.days.length) {
      this.flipStates.days = new Array(newDayDigits.length).fill(false);
      this.flipStates.hours = new Array(newHourDigits.length).fill(false);
      this.flipStates.minutes = new Array(newMinuteDigits.length).fill(false);
      this.flipStates.seconds = new Array(newSecondDigits.length).fill(false);
    }

    this.flipStates.days = newDayDigits.map(
      (digit, i) => digit !== this.dayDigits[i]
    );
    this.flipStates.hours = newHourDigits.map(
      (digit, i) => digit !== this.hourDigits[i]
    );
    this.flipStates.minutes = newMinuteDigits.map(
      (digit, i) => digit !== this.minuteDigits[i]
    );
    this.flipStates.seconds = newSecondDigits.map(
      (digit, i) => digit !== this.secondDigits[i]
    );

    this.nextDayDigits = newDayDigits;
    this.nextHourDigits = newHourDigits;
    this.nextMinuteDigits = newMinuteDigits;
    this.nextSecondDigits = newSecondDigits;

    setTimeout(() => {
      this.days = newDays;
      this.hours = newHours;
      this.minutes = newMinutes;
      this.seconds = newSeconds;
      this.dayDigits = newDayDigits;
      this.hourDigits = newHourDigits;
      this.minuteDigits = newMinuteDigits;
      this.secondDigits = newSecondDigits;
      this.flipStates = {
        days: new Array(newDayDigits.length).fill(false),
        hours: new Array(newHourDigits.length).fill(false),
        minutes: new Array(newMinuteDigits.length).fill(false),
        seconds: new Array(newSecondDigits.length).fill(false),
      };
    }, 600);

    this.ariaLabel = `${this.days} days, ${this.hours} hours, ${this.minutes} minutes, ${this.seconds} seconds remaining`;
  }
}
